import { select, Skill, SkillLevel } from "gamedata/GameData";
import { useLoaderData, useParams } from "react-router";
import { z } from "zod";
import { InputMD } from "~/components/form/InputMd";
import { ClassLink } from "~/components/npc/ClassLink";
import { Comments, getComments } from "~/components/site/Comments";
import { NotFoundInfo } from "~/components/site/NotFoundInfo";
import { SkillInfoBox } from "~/components/skill/SkillInfoBox";
import { SkillLink } from "~/components/skill/SkillLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { Icon } from "~/components/ui/Icon";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { Tabs } from "~/components/ui/Tabs";
import { GameVer, tt, useRealm, useT, useTurl } from "~/util/Lang";
import { MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import { stripSlashes } from "~/util/common";
import { getDescription } from "~/util/skill";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

type SkillAlt = {
  id: number;
  cardName: string;
  level: number;
  name: string;
  icon: string;
  gver: GameVer;
};

async function loadData(
  la: OurLoaderArgs,
): Promise<
  | { status: "404"; alts: SkillAlt[] }
  | { status: "ok"; data: Awaited<ReturnType<typeof makeData>> }
> {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);
  const [idStr] = params.skillId.split("-", 2);
  const [, lvlStr] = params.skillId.split(":", 2);
  const id = parseInt(idStr);
  const levelValue = parseInt(lvlStr ?? "1");
  const skill = la.gd.skills.get(id);
  const level = skill?.levels.get(levelValue);

  if (skill == null || level == null) {
    la.setStatusCode(404);
    const alts: SkillAlt[] = [];
    for (const altGD of Object.values(la.gd.all_gd!)) {
      const altSkill = altGD.skills.get(id);
      const altLevel = altSkill?.levels.get(levelValue);
      if (altSkill && altLevel) {
        alts.push({
          id: altSkill.id,
          cardName: altSkill.cardName,
          level: levelValue,
          name: t(altSkill.name),
          icon: altSkill.icon,
          gver: altGD.gver,
        });
      }
    }
    return { status: "404", alts };
  }

  return { status: "ok", data: await makeData(la, skill, level) };
}

const Params = z.object({ skillId: z.string() });
async function makeData(la: OurLoaderArgs, skill: Skill, level: SkillLevel) {
  const t = tt.bind(null, la.realm.lang);

  // Log
  logPageView(la);
  //

  if (level == null) {
    throw Response.json(
      { msg: t({ en: "Skill Level not found.", ru: "Скилл не найден." }) },
      { status: 404 },
    );
  }

  // const realm = la.realm;
  // const ideal = tturl(realm, `/skills/${skill.cardName}:${level.level ?? 1}`);

  // if (ideal !== la.url.pathname) {
  //   throw redirect(ideal, 301);
  // }

  const data = {
    card: select(skill, {
      id: true,
      cardName: true,
      name: { en: true, ru: true },
      levels: () => Array.from(skill.levels.values()),
      enchanting: () =>
        Array.from(skill.enchanting, ([key, value]) => {
          return { key, value };
        }),
      profs: {
        select: {
          prof: {
            profName: true,
            name: t,
          },
          study: {
            select: {
              level: true,
              skills: {
                select: {
                  skillName: true,
                  icon: true,
                  level: true,
                },
              },
            },
          },
        },
      },
    }),
    skill: select(level, {
      id: true,
      skillName: true,
      level: true,
      icon: true,
      name: { en: true, ru: true },
      desc: { en: true, ru: true },
      operateType: true,
      mp_consume1: true,
      mp_consume2: true,
      hp_consume: true,
      effects: {
        select: { effectName: true, app: (x) => x, per: true, value: true },
      },
    }),
  };

  return {
    ...data,
    comments: await getComments({
      id: data.skill.id + "",
      type: "skill",
      args: la,
    }),
    user: la.req.sess?.user,
  };
}

type LoaderData = Awaited<ReturnType<typeof loader>>;

export const loader = async (la: OurLoaderArgs) => {
  const data = await loadData(la);
  return { ...data, authUser: la.req.sess?.user };
};

export const meta: MetaFunc<LoaderData> = (cx) => {
  if (cx.data.status === "404") {
    return {
      title: "Skill not found - " + homePage(cx.realm),
    };
  }

  const data = cx.data.data;
  const name = `${cx.t(data.skill.name)} ${cx.t({ en: "Lvl.", ru: "Ур." })} ${
    data.skill.level
  }`;

  return {
    title: `${name} - ${cx.t({ en: "Skills", ru: "Умения" })} - ${homePage(
      cx.realm,
    )}`,
    description: getDescription({ realm: cx.realm, skill: data.skill }),
    ogTitle: `${cx.t({ en: "Skill", ru: "Умение" })}: ${name}`,
    canonicalUrl: `/skills/${data.card.cardName}:${data.skill.level}`,
  };
};

export default function SkillPage() {
  const t = useT();
  const turl = useTurl();
  const realm = useRealm();
  const params = useParams();
  const loaderData = useLoaderData<typeof loader>();

  if (loaderData.status === "404") {
    return (
      <NotFoundInfo
        title={t({ en: "Skill not found", ru: "Скил не найден" })}
        altsTitle={t({
          en: "This skill exists in other gave versions:",
          ru: "Этот скил доступен в других версиях игры:",
        })}
        alts={loaderData.alts.map((altSkill) => ({
          gver: altSkill.gver,
          node: <SkillLink data={altSkill} />,
        }))}
      />
    );
  }

  const data = loaderData.data;

  const [, lvlStr] = Params.parse(params).skillId.split(":", 2);

  const breadcrumbList = [
    { title: t({ en: "Skills", ru: "Умения" }), url: turl("/skills") },
    {
      title: `${t(data.skill.name)}`.trim(),
      url: turl(`/items/${data.card.cardName}`),
    },
  ];

  return (
    <div>
      <div className="content">
        <Breadcrumb items={breadcrumbList} />
        <div className="columns">
          <div className="column is-three-quarters">
            <PageTitle>{t(data.skill.name)}</PageTitle>
            <div>
              <div
                className="item-box"
                style={{ background: "#292624", padding: 10 }}
              >
                <SkillInfoBox skill={data.skill} />
              </div>
              {/* {effects.length ? <div
                className="item-box"
                style={{ background: "#292624", padding: 10 }}
              >
                <pre>
                  <div>Effects:</div>
                  {JSON.stringify(effects, null, 2)}</pre>
              </div> : null} */}
            </div>
          </div>
          <div className="column">
            <InfoBox
              title={t({
                en: "Skill Facts",
                ru: "Краткая информация",
              })}
            >
              <Facts
                facts={[
                  {
                    name: "Skill ID",
                    value: data.skill.id,
                  },
                  {
                    name: "Skill Level",
                    value: data.skill.level,
                  },
                  {
                    name: "Англ. название",
                    value: data.skill.name.en,
                    show: realm.lang === "ru",
                  },
                  {
                    name: "Type",
                    value: data.skill.operateType,
                  },
                  {
                    name: "MP Consume",
                    value: (
                      <span style={{ color: "#008cff" }}>
                        {data.skill.mp_consume1 + data.skill.mp_consume2}
                      </span>
                    ),
                  },
                  {
                    name: "HP Consume",
                    value: (
                      <span style={{ color: "red" }}>
                        {data.skill.hp_consume}
                      </span>
                    ),
                  },
                ]}
              />
              <InputMD
                value={`:skill[${data.card.id}]{lvl=${data.skill.level}}`}
              />
            </InfoBox>
          </div>
        </div>
      </div>
      <Tabs
        current={params["*"] ?? ""}
        link={(id) =>
          turl(
            stripSlashes(
              `/skills/${data.card.cardName}:${data.skill.level}/${id}`,
            ),
          )
        }
        tabs={[
          {
            id: "comments",
            title: t({ en: "Comments", ru: "Комментарии" }),
            body: () => (
              <div>
                <Comments
                  comments={data.comments}
                  authUser={data.user}
                  subject={{ objType: "skill", objUname: data.skill.id + "" }}
                />
              </div>
            ),
          },
          {
            id: "levels",
            title: t({ en: "Levels", ru: "Уровни" }),
            body: () => (
              <Table
                data={data.card.levels}
                id={(_skill, i) => i}
                cols={[
                  {
                    title: "",
                    cell: (skill) => <Icon icon={skill.icon} />,
                    thPadding: "0",
                    padding: "8px 0 8px 8px",
                    width: 32 + 12,
                  },
                  {
                    title: t({ en: "Name", ru: "Название" }),
                    cell: (skill) => (
                      <>
                        <SkillLink
                          data={{
                            ...skill,
                            cardName: data.card.cardName,
                            name: t(skill.name),
                          }}
                          showIcon={false}
                        />
                        <div style={{ fontSize: "14px" }}>{t(skill.desc)}</div>
                      </>
                    ),
                  },
                  {
                    title: t({ en: "MP/HP", ru: "МП/ХП" }),
                    cell: (skill) => (
                      <span>
                        <span style={{ color: "#008cff" }}>
                          {Math.round(
                            skill.mp_consume1 ?? 0 + skill.mp_consume2 ?? 0,
                          )}
                        </span>
                        <span style={{ padding: "0 4px" }}>/</span>
                        <span style={{ color: "red" }}>
                          {Math.round(skill.hp_consume ?? 0)}
                        </span>
                      </span>
                    ),
                  },
                  {
                    title: t({ en: "Lvl", ru: "Уровень" }),
                    cell: (skill) => skill.level,
                  },
                  {
                    title: t({ en: "Type", ru: "Тип" }),
                    cell: (skill) => skill.operateType,
                  },
                ]}
              />
            ),
          },
          {
            id: "classes",
            title: t({ en: "Classes", ru: "Классы" }),
            body: () => (
              <Table
                data={data.card.profs}
                id={(_skill, i) => i}
                cols={[
                  {
                    title: "",
                    cell: ({ prof }) => <Icon icon={prof.profName} />,
                    thPadding: "0",
                    padding: "8px 0 8px 8px",
                    width: 32 + 12,
                  },
                  {
                    title: t({ en: "Name", ru: "Имя" }),
                    cell: ({ prof }) => <ClassLink prof={prof} />,
                  },
                  {
                    title: t({
                      en: "Levels of Learning",
                      ru: "Уровни изучения",
                    }),
                    cell: (prof) => (
                      <div style={{ display: "flex", gap: 5 }}>
                        {prof.study.map((st, index) => (
                          <div
                            key={index}
                            style={{
                              background: "#00000026",
                              padding: 5,
                              borderRadius: 5,
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{ paddingBottom: 5, fontWeight: "bold" }}
                            >
                              {st.level}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 5,
                              }}
                            >
                              {st.skills.map((s, index) => (
                                <div
                                  key={index}
                                  style={{
                                    width: 30,
                                    height: 30,
                                    position: "relative",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 30,
                                      height: 30,
                                      borderRadius: 2,
                                      background:
                                        s.level === (parseInt(lvlStr) ?? 1)
                                          ? "#232413c7"
                                          : "#000000c7",
                                      position: "absolute",
                                      border:
                                        s.level === (parseInt(lvlStr) ?? 1)
                                          ? "1px solid #ffd717"
                                          : "1px solid #505050",
                                      color:
                                        s.level === (parseInt(lvlStr) ?? 1)
                                          ? "#ffda23"
                                          : "#fff",
                                      fontSize: 13,
                                      textShadow: "0px 0px 1px #000000",
                                    }}
                                  >
                                    {s.level}
                                  </div>
                                  <Icon icon={s.icon} size={30} />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            ),
          },
          ...(data.card.enchanting
            ? data.card.enchanting.map((list, i) => {
                return {
                  id: "enchanting-" + (i + 1),
                  title: t({
                    en: `Enchanting (${list.key})`,
                    ru: `Улучшение (${list.key})`,
                  }),
                  body: () => (
                    <Table
                      data={list.value}
                      id={(_skill, i) => i}
                      cols={[
                        {
                          title: "",
                          cell: (enchant) => <Icon icon={enchant.skill.icon} />,
                          thPadding: "0",
                          padding: "8px 0 8px 8px",
                          width: 32 + 12,
                        },
                        {
                          title: t({ en: "Name", ru: "Название" }),
                          cell: (enchant) => (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  columnGap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                <div style={{ color: "#51a0ef" }}>
                                  {t(enchant.skill.name)}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#f7d55a",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  + {enchant.level} {enchant.type}
                                </div>
                              </div>
                              <div style={{ fontSize: "14px" }}>
                                {t(enchant.skill.desc)}
                              </div>
                            </>
                          ),
                        },
                        {
                          title: t({ en: "MP/HP", ru: "МП/ХП" }),
                          cell: (enchant) => (
                            <span>
                              <span style={{ color: "#008cff" }}>
                                {Math.round(
                                  enchant.skill.mp_consume1 ??
                                    0 + enchant.skill.mp_consume2 ??
                                    0,
                                )}
                              </span>
                              <span style={{ padding: "0 4px" }}>/</span>
                              <span style={{ color: "red" }}>
                                {Math.round(enchant.skill.hp_consume ?? 0)}
                              </span>
                            </span>
                          ),
                        },
                        // {
                        //   title: t({ en: "Lvl", ru: "Уровень" }),
                        //   cell: (enchant) => enchant.level,
                        // },
                        {
                          title: t({ en: "Chance", ru: "Шанс" }),
                          cell: (enchant) => (
                            <div
                              style={{ display: "flex", gap: 10, fontSize: 13 }}
                            >
                              <div
                                style={{
                                  textTransform: "uppercase",
                                  fontSize: 11,
                                  padding: "3px 0",
                                }}
                              >
                                <div style={{ paddingBottom: 3 }}>Level:</div>
                                <div>Chance:</div>
                              </div>
                              {enchant.chance.map((c, index) => (
                                <div
                                  key={index}
                                  style={{
                                    background: "#ffffff14",
                                    padding: "2px 7px",
                                    borderRadius: 5,
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      borderBottom: "1px solid #ffffff1c",
                                    }}
                                  >
                                    {c[0]}
                                  </div>
                                  <div>{c[1]}%</div>
                                </div>
                              ))}
                            </div>
                          ),
                        },
                      ]}
                    />
                  ),
                };
              })
            : []),
        ]}
      />
    </div>
  );
}
